import type { TodoDocument } from '../types/sync';

const MAX_TIMER_DELAY = 2_147_000_000;
const REMINDER_WORKER_PATH = '/todo-reminder-sw.js';

export type TodoReminderCapability = 'system' | 'in-app';

export function todoReminderDelay(todo: TodoDocument, now = Date.now()) {
  if (!todo.reminderEnabled || todo.isCompleted || !todo.dueAt) return null;
  const dueAt = Date.parse(todo.dueAt);
  if (!Number.isFinite(dueAt) || dueAt <= now) return null;
  return dueAt - now;
}

export function scheduleTodoReminders(
  todos: TodoDocument[],
  onDue: (todo: TodoDocument) => void,
  now: () => number = Date.now,
) {
  const timers = new Set<ReturnType<typeof setTimeout>>();

  const schedule = (todo: TodoDocument) => {
    const remaining = todoReminderDelay(todo, now());
    if (remaining === null) return;
    const timer = setTimeout(() => {
      timers.delete(timer);
      const latestRemaining = todoReminderDelay(todo, now());
      if (latestRemaining === null) {
        if (todo.dueAt && Date.parse(todo.dueAt) <= now()) onDue(todo);
        return;
      }
      if (latestRemaining > MAX_TIMER_DELAY) schedule(todo);
      else {
        const dueTimer = setTimeout(() => {
          timers.delete(dueTimer);
          onDue(todo);
        }, latestRemaining);
        timers.add(dueTimer);
      }
    }, Math.min(remaining, MAX_TIMER_DELAY));
    timers.add(timer);
  };

  todos.forEach(schedule);
  return () => {
    timers.forEach(timer => clearTimeout(timer));
    timers.clear();
  };
}

export async function requestTodoReminderPermission(): Promise<TodoReminderCapability> {
  if (!supportsSystemNotifications()) return 'in-app';
  try {
    const permission = Notification.permission === 'default'
      ? await Notification.requestPermission()
      : Notification.permission;
    if (permission !== 'granted') return 'in-app';
    await ensureReminderWorker();
    return 'system';
  } catch {
    return 'in-app';
  }
}

export async function showSystemTodoReminder(todo: TodoDocument) {
  return showSystemReminder({
    title: '待办到时间了',
    body: todo.detail || `“${todo.title}”已经到截止时间`,
    tag: `aulivis-todo-${todo.id}`,
    url: `/plan?tab=todos&todo=${encodeURIComponent(todo.id)}`,
  });
}

export async function showSystemReminder({ title, body, tag, url }: { title: string; body: string; tag: string; url: string }) {
  if (!supportsSystemNotifications() || Notification.permission !== 'granted') return false;
  const options: NotificationOptions = { body, tag, data: { url } };
  try {
    const registration = await ensureReminderWorker();
    await registration.showNotification(title, options);
    return true;
  } catch {
    try {
      new Notification(title, options);
      return true;
    } catch {
      return false;
    }
  }
}

function supportsSystemNotifications() {
  return typeof window !== 'undefined'
    && window.isSecureContext
    && typeof Notification !== 'undefined'
    && 'serviceWorker' in navigator;
}

async function ensureReminderWorker() {
  const existing = await navigator.serviceWorker.getRegistration(REMINDER_WORKER_PATH);
  return existing ?? navigator.serviceWorker.register(REMINDER_WORKER_PATH, { scope: '/' });
}
